import * as m from '@staiter/i18n';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { TrendingUp } from 'lucide-react';
import { useState } from 'react';
import { BentoCard, EmptyState, ExportMenu, PageHeader } from '~/components/data';
// S-FIX-2-FE.5.4: canonical HeroMetric (с trend chip + tabular-nums + 48px hero size).
import { HeroMetric } from '~/components/domain';
import { DateRangePicker } from '~/components/primitives';
import { fetchPnl } from '~/lib/api/finance';
import { formatMoney } from '~/lib/format';
import styles from './_auth.finance.pnl.module.css';

/**
 * S15-FE.3 — P&L report page (hero metrics + margin + breakdown sections).
 */

export const Route = createFileRoute('/_auth/finance/pnl')({ component: FinancePnlPage });

function today(): string {
  return new Date().toISOString().slice(0, 10);
}
// S-FIX-6: default YTD (см. cash-flow для обоснования).
function firstOfYear(): string {
  const d = new Date();
  return `${d.getUTCFullYear()}-01-01`;
}

// S-FIX-2-FE.5.4: локальный HeroMetric удалён в пользу canonical
// `~/components/domain/HeroMetric` — единый компонент во всём приложении
// (STAITER_UI.md §4.3 Progressive Disclosure: max 3 на базовом экране).

function FinancePnlPage(): JSX.Element {
  const [dateFrom, setDateFrom] = useState(firstOfYear());
  const [dateTo, setDateTo] = useState(today());
  const query = useQuery({
    queryKey: ['finance', 'pnl', dateFrom, dateTo],
    queryFn: () => fetchPnl({ date_from: dateFrom, date_to: dateTo }),
    enabled: Boolean(dateFrom && dateTo),
  });
  const p = query.data;

  return (
    <div>
      {/* FE-157 Sprint Q+: ExportMenu CTA в PageHeader actions slot. */}
      <PageHeader
        title={m.phase2_finance_pnl_title()}
        actions={
          <ExportMenu resource="finance-pnl" filters={{ date_from: dateFrom, date_to: dateTo }} />
        }
      />

      <div className={styles.filterRow}>
        {/* S-FIX-5 fix: native date input запрещён → DateRangePicker. */}
        <DateRangePicker
          label={m.cogs_filter_date_from()}
          value={{ from: dateFrom || null, to: dateTo || null }}
          onChange={(r) => {
            setDateFrom(r.from ?? '');
            setDateTo(r.to ?? '');
          }}
        />
      </div>

      {!p && !query.isLoading ? (
        <EmptyState title={m.phase2_finance_pnl_title()} icon={<TrendingUp size={32} />} />
      ) : p ? (
        <>
          <div className={styles.heroGrid}>
            {/* FE-13 (QA 2026-05-31): STAITER_UI.md §4.3 — max 3 hero на base level.
                Secondary метрики (cogs/opex/operating_profit/tax) перенесены в Bento
                breakdown card ниже для Progressive Disclosure. */}
            <HeroMetric label={m.pnl_revenue()} value={p.revenue} compact />
            <HeroMetric label={m.pnl_gross_profit()} value={p.gross_profit} compact />
            <HeroMetric label={m.pnl_net_profit()} value={p.net_profit} compact />
          </div>
          <BentoCard title={m.pnl_section_breakdown()}>
            <dl className={styles.breakdownList}>
              <div className={styles.breakdownRow}>
                <dt>{m.pnl_cogs()}</dt>
                <dd>{formatMoney(p.cogs)}</dd>
              </div>
              <div className={styles.breakdownRow}>
                <dt>{m.pnl_opex()}</dt>
                <dd>{formatMoney(p.opex)}</dd>
              </div>
              <div className={styles.breakdownRow}>
                <dt>{m.pnl_operating_profit()}</dt>
                <dd>{formatMoney(p.operating_profit)}</dd>
              </div>
              <div className={styles.breakdownRow}>
                <dt>{m.pnl_tax()}</dt>
                <dd>{formatMoney(p.tax)}</dd>
              </div>
            </dl>
          </BentoCard>
          <div className={styles.marginRow}>
            <div>
              {m.pnl_gross_margin()}:{' '}
              <strong>{Number(p.gross_margin_pct.amount).toFixed(2)}%</strong>
            </div>
            <div>
              {m.pnl_net_margin()}: <strong>{Number(p.net_margin_pct.amount).toFixed(2)}%</strong>
            </div>
          </div>
          {/* S-FIX-1-FE.D.1 — FX section рендерится UNCONDITIONALLY (fx_gain_loss теперь required). */}
          <div className={styles.fxCard}>
            <h3 className={styles.fxTitle}>{m.pnl_section_fx_gain_loss()}</h3>
            {p.legacy_schema && (
              <div className={styles.legacyWarning}>{m.pnl_legacy_schema_warning()}</div>
            )}
            {p.fx_gain_loss.unrealized_amount_kzt === '0' &&
            p.fx_gain_loss.realized_amount_kzt === '0' ? (
              <div className={styles.fxEmpty}>{m.pnl_fx_no_adjustments_in_period()}</div>
            ) : (
              <div className={styles.fxRow} title={m.pnl_fx_explainer()}>
                <div>
                  {m.pnl_fx_unrealized_label()}:{' '}
                  <strong>{p.fx_gain_loss.unrealized_amount_kzt} KZT</strong>
                </div>
                <div>
                  {m.pnl_fx_realized_label()}:{' '}
                  <strong>{p.fx_gain_loss.realized_amount_kzt} KZT</strong>
                </div>
                <div className={styles.fxRowProvenance}>· {m.pnl_fx_provenance()}</div>
              </div>
            )}
            {p.fx_gain_loss.included_kinds.length < 2 && (
              <div className={styles.fxPartialWarning}>{m.pnl_fx_partial_coverage_warning()}</div>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}
